import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useUser } from "../context/auth";
import {
  getAbsoluteExpiresAt,
  getLastRefreshAt,
  getSessionExpiresAt,
  refreshSession,
  REFRESH_THROTTLE_MS,
  SESSION_EXPIRES_AT_KEY,
} from "../utils/sessionClock";

// Loaded only when a warning is due, to keep the dialog out of the main bundle.
const SessionTimeoutWarning = lazy(() => import("./SessionTimeoutWarning"));

const WARNING_LEAD_MS = 60 * 1000;
const ACTIVITY_CHECK_THROTTLE_MS = 5 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "wheel", "scroll", "touchstart"];
// Capture so scrolls inside panels and tables count, not just the window's own scroll.
const LISTENER_OPTIONS = { capture: true, passive: true };

// Keeps the server session alive while the user is active, and signs them out when it
// lapses. The server enforces the timeout; this makes the browser follow it instead of
// finding out on the next failed request.
//
// onExpired(reason) is called once per session: "idle" when it ran out, "signed-out" when
// the user chose to sign out from the warning, "elsewhere" when another tab signed out.
const SessionTimeoutManager = ({ onExpired }) => {
  const { isUserAuthenticated } = useUser();
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [isFinalWarning, setIsFinalWarning] = useState(false);
  const isExpiringRef = useRef(false);
  const isSyncingRef = useRef(false);
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  const expire = useCallback((reason) => {
    if (isExpiringRef.current) return;
    isExpiringRef.current = true;
    setSecondsLeft(null);
    onExpiredRef.current(reason);
  }, []);

  const refresh = useCallback(
    async (options) => {
      const result = await refreshSession(options);
      if (result === "expired") expire("idle");
    },
    [expire],
  );

  // With no usable clock (OAuth sign-in, a session from before this release, cleared
  // storage), ask the server where the session stands before judging it expired.
  useEffect(() => {
    if (!isUserAuthenticated) {
      isExpiringRef.current = false;
      setSecondsLeft(null);
      return;
    }

    const expiresAt = getSessionExpiresAt();
    if (expiresAt && expiresAt > Date.now()) return;

    isSyncingRef.current = true;
    refresh({ force: true }).finally(() => {
      isSyncingRef.current = false;
    });
  }, [isUserAuthenticated, refresh]);

  useEffect(() => {
    if (!isUserAuthenticated) return undefined;

    let lastCheckAt = 0;
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastCheckAt < ACTIVITY_CHECK_THROTTLE_MS) return;
      lastCheckAt = now;

      // Once the warning is up, only its buttons count. Otherwise moving the mouse toward
      // a button would act on the warning before the user could choose.
      const expiresAt = getSessionExpiresAt();
      if (expiresAt && expiresAt - now <= WARNING_LEAD_MS) return;

      const lastRefreshAt = getLastRefreshAt();
      if (lastRefreshAt && now - lastRefreshAt < REFRESH_THROTTLE_MS) return;
      refresh();
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, LISTENER_OPTIONS),
    );
    return () =>
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity, LISTENER_OPTIONS),
      );
  }, [isUserAuthenticated, refresh]);

  // Polls rather than scheduling one long timer: timers don't run while a laptop sleeps,
  // and another tab can move the expiry at any time.
  useEffect(() => {
    if (!isUserAuthenticated) return undefined;

    const tick = () => {
      const expiresAt = getSessionExpiresAt();
      if (!expiresAt || isSyncingRef.current) return;

      const remainingMs = expiresAt - Date.now();
      if (remainingMs <= 0) {
        expire("idle");
      } else if (remainingMs <= WARNING_LEAD_MS) {
        const absoluteExpiresAt = getAbsoluteExpiresAt();
        setIsFinalWarning(Boolean(absoluteExpiresAt && absoluteExpiresAt <= expiresAt));
        setSecondsLeft(Math.ceil(remainingMs / 1000));
      } else {
        setSecondsLeft(null);
      }
    };

    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [isUserAuthenticated, expire]);

  // Sign-out clears the clock, so another tab removing it means this tab is signed out too.
  useEffect(() => {
    if (!isUserAuthenticated) return undefined;

    const handleStorage = (event) => {
      // A null key means storage was cleared outright.
      if (event.key !== SESSION_EXPIRES_AT_KEY && event.key !== null) return;
      if (getSessionExpiresAt()) return;

      const previousExpiresAt = Number(event.oldValue);
      expire(previousExpiresAt && previousExpiresAt <= Date.now() ? "idle" : "elsewhere");
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [isUserAuthenticated, expire]);

  if (secondsLeft === null) return null;

  return (
    <Suspense fallback={null}>
      <SessionTimeoutWarning
        secondsLeft={secondsLeft}
        isFinal={isFinalWarning}
        onStaySignedIn={() => refresh()}
        onSignOut={() => expire("signed-out")}
      />
    </Suspense>
  );
};

export default SessionTimeoutManager;
